import * as React from 'react'
import { Check } from 'lucide-react'

import type { Asset, AssetFolder } from '@/types'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AssetGridPickerProps {
  label: string
  assets: Asset[]
  folders: AssetFolder[]
  selectedId: number | null
  onSelect: (asset: Asset) => void
  filterFolderIds?: number[]
  filterSceneType?: string
  showSearch?: boolean
  showFolderFilter?: boolean
  showSort?: boolean
}

type SortOption = 'newest' | 'oldest' | 'name'

// ─── Component ────────────────────────────────────────────────────────────────

export function AssetGridPicker({
  label,
  assets,
  folders,
  selectedId,
  onSelect,
  filterFolderIds,
  filterSceneType,
  showSearch = true,
  showFolderFilter = true,
  showSort = true,
}: AssetGridPickerProps) {
  const [search, setSearch] = React.useState('')
  const [folderFilter, setFolderFilter] = React.useState<number | 'all'>('all')
  const [sort, setSort] = React.useState<SortOption>('newest')

  // ── Pre-filter by folder IDs and scene type ───────────────────────────────

  const baseAssets = React.useMemo(() => {
    let filtered = assets.filter((a) => a.file_type === 'image')
    if (filterFolderIds && filterFolderIds.length > 0) {
      const idSet = new Set(filterFolderIds)
      filtered = filtered.filter((a) => idSet.has(a.folder_id))
    }
    if (filterSceneType) {
      filtered = filtered.filter((a) => a.scene_type === filterSceneType)
    }
    return filtered
  }, [assets, filterFolderIds, filterSceneType])

  // ── Folders that actually have assets in this subset ─────────────────────

  const relevantFolderIds = React.useMemo(
    () => new Set(baseAssets.map((a) => a.folder_id)),
    [baseAssets]
  )
  const relevantFolders = React.useMemo(
    () => folders.filter((f) => relevantFolderIds.has(f.id)),
    [folders, relevantFolderIds]
  )

  // ── Search + folder filter + sort ─────────────────────────────────────────

  const displayed = React.useMemo(() => {
    let result = baseAssets

    // Folder filter
    if (folderFilter !== 'all') {
      result = result.filter((a) => a.folder_id === folderFilter)
    }

    // Search
    const q = search.trim().toLowerCase()
    if (q) {
      result = result.filter((a) => {
        const name = (a.original_filename ?? a.filename).toLowerCase()
        const desc = (a.ai_description ?? '').toLowerCase()
        const tags = (a.ai_tags ?? []).join(' ').toLowerCase()
        return name.includes(q) || desc.includes(q) || tags.includes(q)
      })
    }

    // Sort
    const copy = [...result]
    if (sort === 'newest') {
      copy.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    } else if (sort === 'oldest') {
      copy.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    } else {
      copy.sort((a, b) =>
        (a.original_filename ?? a.filename).localeCompare(b.original_filename ?? b.filename)
      )
    }
    return copy
  }, [baseAssets, folderFilter, search, sort])

  const selectedAsset = React.useMemo(
    () => (selectedId !== null ? assets.find((a) => a.id === selectedId) ?? null : null),
    [assets, selectedId]
  )

  const gridRef = React.useRef<HTMLDivElement>(null)

  const handleSelect = (asset: Asset) => {
    onSelect(asset)
  }

  // ── Empty state ────────────────────────────────────────────────────────────

  if (baseAssets.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground py-4">
          {filterSceneType === 'model'
            ? 'No avatars found. Generate some in Avatar Studio first.'
            : 'No images found. Upload some assets first.'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3" ref={gridRef}>
      {/* Label */}
      <p className="text-sm font-medium text-foreground">{label}</p>

      {/* Controls */}
      {(showSearch || showFolderFilter || showSort) && (
        <div className="flex flex-wrap gap-2">
          {showSearch && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="flex-1 min-w-[160px] rounded-md border border-input bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label={`Search ${label}`}
            />
          )}
          {showFolderFilter && relevantFolders.length > 1 && (
            <select
              value={folderFilter}
              onChange={(e) =>
                setFolderFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Filter by folder"
            >
              <option value="all">Folder: All</option>
              {relevantFolders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
          {showSort && (
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOption)}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Sort order"
            >
              <option value="newest">Sort: Newest</option>
              <option value="oldest">Oldest</option>
              <option value="name">Name A-Z</option>
            </select>
          )}
        </div>
      )}

      {/* Grid */}
      {displayed.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No results match your search.</p>
      ) : (
        <div
          className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2"
          role="listbox"
          aria-label={label}
        >
          {displayed.map((asset) => {
            const isSelected = asset.id === selectedId
            const name = asset.original_filename ?? asset.filename
            return (
              <button
                key={asset.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                aria-label={name}
                onClick={() => handleSelect(asset)}
                className={[
                  'group relative aspect-square rounded-md overflow-hidden border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isSelected
                    ? 'border-foreground ring-2 ring-foreground'
                    : 'border-border hover:border-foreground/40',
                ].join(' ')}
              >
                <img
                  src={`/uploads/${asset.file_path}`}
                  alt={name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {isSelected && (
                  <div
                    className="absolute inset-0 bg-foreground/20 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <div className="bg-foreground rounded-full p-1">
                      <Check className="h-3 w-3 text-background" />
                    </div>
                  </div>
                )}
                <p className="absolute bottom-0 left-0 right-0 bg-background/80 px-1 py-0.5 text-[10px] truncate text-foreground">
                  {name}
                </p>
              </button>
            )
          })}
        </div>
      )}

      {/* Selected summary bar */}
      {selectedAsset !== null && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
          <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate font-medium">
            Selected: {selectedAsset.original_filename ?? selectedAsset.filename}
          </span>
          <button
            type="button"
            onClick={() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="ml-auto shrink-0 text-muted-foreground hover:text-foreground underline"
          >
            Change
          </button>
        </div>
      )}
    </div>
  )
}
